/* eslint-disable @repo/internal/react/require-jsdoc */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useEffect } from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import ChartTrendUpIcon from '@atlaskit/icon/core/chart-trend-up';
import { Box, Stack, Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { LinkItem } from './items';

const POPULAR_LINKS = [
	{
		title: 'Icon explorer',
		description: 'An icon is a symbol representing a command, device, directory, or common action.',
		path: '/components/icon/icon-explorer',
	},
	{
		title: 'All tokens',
		description: 'Design tokens are the single source of truth to name and store design decisions.',
		path: '/components/tokens/all-tokens',
	},
	{
		title: 'Button',
		description: 'A button triggers an event or action. They let users know what will happen next.',
		path: '/components/button',
	},
	{
		title: 'Color',
		description: 'Color distinguishes our brand and reinforces consistent experiences across apps.',
		path: '/foundations/color',
	},
	{
		title: 'Typography',
		description:
			"Typography is our system of fonts and text styles. It enhances communication, reinforces brand, and guides users' emotions.",
		path: '/foundations/typography',
	},
];

const styles = cssMap({
	headingItem: {
		display: 'flex',
		alignItems: 'center',
		paddingBlock: token('space.150'),
		paddingInline: token('space.150'),
		marginInline: token('space.200'),
		borderRadius: token('radius.large'),
		gap: token('space.100'),
		backgroundColor: token('color.background.accent.gray.subtlest'),
	},
	headingIcon: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '24px',
		height: '24px',
		borderRadius: token('radius.small'),
		backgroundColor: token('color.background.accent.gray.subtle'),
	},
	resultsContainer: {
		marginInlineStart: token('space.300'),
		marginInlineEnd: token('space.200'),
		paddingBlockStart: token('space.050'),
		borderInlineStartWidth: token('border.width'),
		borderInlineStartStyle: 'solid',
		borderImage: `linear-gradient(to bottom, ${token('color.border')}, ${token('elevation.surface.overlay')}) 1 100%`,
	},
});

type PopularResultsProps = {
	activeIndex: number;
	onResultsCountChange(count: number): void;
	onResultClick?(): void;
	listboxId: string;
	resultRef?: (index: number) => (el: HTMLAnchorElement | null) => void;
};

const PopularResults = ({
	activeIndex,
	onResultsCountChange,
	onResultClick,
	listboxId,
	resultRef,
}: PopularResultsProps): JSX.Element => {
	useEffect(() => {
		onResultsCountChange(POPULAR_LINKS.length);
	}, [onResultsCountChange]);

	return (
		<Box>
			<Box xcss={styles.headingItem} role="presentation" aria-hidden="true">
				<Box xcss={styles.headingIcon}>
					<ChartTrendUpIcon label="" color={token('color.icon.inverse')} size="small" />
				</Box>
				<Heading size="small" as="h2" aria-hidden="true">
					Popular
				</Heading>
			</Box>
			<Box xcss={styles.resultsContainer}>
				{POPULAR_LINKS.map((link, index) => (
					<LinkItem
						key={link.path}
						to={link.path}
						onResultClick={onResultClick}
						isKeyboardActive={index === activeIndex}
						optionId={`${listboxId}-option-${index}`}
						posInSet={index + 1}
						setSize={POPULAR_LINKS.length}
						resultRef={resultRef?.(index)}
						shouldTitleWrap
					>
						<Stack space="space.050">
							<Text
								weight="bold"
								color={index === activeIndex ? 'color.text.selected' : 'color.text'}
							>
								{link.title}
							</Text>
							<Text
								as="p"
								color={index === activeIndex ? 'color.text.selected' : 'color.text.subtle'}
							>
								{link.description}
							</Text>
						</Stack>
					</LinkItem>
				))}
			</Box>
		</Box>
	);
};

export default PopularResults;
